import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import {
  Archive,
  ArrowLeft,
  Check,
  ChevronDown,
  Download,
  FileText,
  Forward,
  Inbox as InboxIcon,
  Mail,
  MailOpen,
  MessageSquare,
  MoreHorizontal,
  Paperclip,
  PenLine,
  RefreshCw,
  Reply,
  RotateCcw,
  Search,
  Send,
  Star,
  Trash2,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import {
  adminApi,
  type Application,
  type ContactMessage,
  type Email,
  type EmailAttachment,
  type EmailDraftInput,
  type EmailStateChanges,
} from '../../lib/adminApi';
import { refreshAdminOverview } from '../../lib/adminEvents';
import { resolveEmailDisplayName, splitEmailBody, type EmailIdentitySources } from '../../lib/emailDisplay';

type Thread = {
  threadId: string;
  messages: Email[];
  latest: Email;
  unread: boolean;
  starred: boolean;
  counterpart: string;
  counterpartEmail: string;
};

type MailFolder = 'inbox' | 'starred' | 'sent' | 'drafts' | 'archive' | 'trash' | 'contacts';
type ComposeState = { id: string | null; threadId?: string; toEmail: string; subject: string; text: string; dirty: boolean };

const FOLDERS: { id: MailFolder; label: string; icon: LucideIcon }[] = [
  { id: 'inbox', label: 'Inbox', icon: InboxIcon },
  { id: 'starred', label: 'Starred', icon: Star },
  { id: 'sent', label: 'Sent', icon: Send },
  { id: 'drafts', label: 'Drafts', icon: FileText },
  { id: 'archive', label: 'Archive', icon: Archive },
  { id: 'trash', label: 'Trash', icon: Trash2 },
  { id: 'contacts', label: 'Website inquiries', icon: MessageSquare },
];

const folderFromParams = (params: URLSearchParams): MailFolder => {
  if (params.get('view') === 'contacts') return 'contacts';
  const value = params.get('folder');
  return FOLDERS.some((folder) => folder.id === value) ? value as MailFolder : 'inbox';
};

function buildThreads(emails: Email[], identities: EmailIdentitySources): Thread[] {
  const groups = new Map<string, Email[]>();
  for (const email of emails) {
    if (email.direction === 'draft') continue;
    const list = groups.get(email.threadId) ?? [];
    list.push(email);
    groups.set(email.threadId, list);
  }
  return [...groups.values()]
    .map((messages) => {
      const ordered = [...messages].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      const latest = ordered[ordered.length - 1];
      const inbound = [...ordered].reverse().find((message) => message.direction === 'inbound');
      const counterpartEmail = inbound?.fromEmail || latest.toEmail;
      const counterpart = resolveEmailDisplayName(
        inbound ?? { fromEmail: latest.toEmail, fromName: null },
        identities,
      );
      return {
        threadId: latest.threadId,
        messages: ordered,
        latest,
        unread: ordered.some((message) => message.direction === 'inbound' && message.isRead === 0),
        starred: ordered.some((message) => (message.isStarred ?? 0) === 1),
        counterpart,
        counterpartEmail,
      };
    })
    .sort((a, b) => b.latest.createdAt.localeCompare(a.latest.createdAt));
}

const isImageAttachment = (attachment: EmailAttachment) => (attachment.contentType || '').toLowerCase().startsWith('image/');

function MessageAttachments({ emailId, attachments }: { emailId: string; attachments: EmailAttachment[] }) {
  if (!attachments.length) return null;
  const images = attachments.filter(isImageAttachment);
  const files = attachments.filter((attachment) => !isImageAttachment(attachment));
  return (
    <div className="cms-message-attachments" aria-label={`${attachments.length} attachment${attachments.length === 1 ? '' : 's'}`}>
      <div className="cms-message-attachments-head"><Paperclip size={13} aria-hidden="true" /><span>{attachments.length} attachment{attachments.length === 1 ? '' : 's'}</span></div>
      {images.length > 0 && (
        <div className="cms-attachment-previews">
          {images.map((attachment) => {
            const url = adminApi.getEmailAttachmentUrl(emailId, attachment.id);
            const filename = attachment.filename?.trim() || 'Image';
            return (
              <a key={attachment.id} className="cms-attachment-preview" href={url} target="_blank" rel="noreferrer" title={`${filename} · ${formatFileSize(attachment.size)}`}>
                <img src={url} alt={filename} loading="lazy" />
                <span className="cms-attachment-preview-cap">{filename}</span>
              </a>
            );
          })}
        </div>
      )}
      {files.length > 0 && (
        <ul>
          {files.map((attachment) => {
            const filename = attachment.filename?.trim() || 'Untitled attachment';
            return (
              <li key={attachment.id}>
                <a href={adminApi.getEmailAttachmentUrl(emailId, attachment.id)} download={filename}>
                  <span><b>{filename}</b><small>{attachment.contentType || 'File'} · {formatFileSize(attachment.size)}</small></span>
                  <Download size={14} aria-hidden="true" />
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(unit === 0 || value >= 10 ? 0 : 1)} ${units[unit]}`;
}

function formatListDate(value: string): string {
  const date = new Date(value);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const elapsed = now.getTime() - date.getTime();
  if (elapsed >= 0 && elapsed < 6 * 24 * 60 * 60 * 1000) return date.toLocaleDateString([], { weekday: 'short' });
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function messageText(email: Pick<Email, 'text' | 'html'>): string {
  if (email.text?.trim()) return email.text;
  if (!email.html?.trim()) return '';
  if (typeof DOMParser === 'undefined') return email.html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const document = new DOMParser().parseFromString(email.html, 'text/html');
  const quote = document.body.querySelector('.gmail_quote, blockquote[type="cite"], blockquote, [id^="divRplyFwdMsg"]');
  if (quote) quote.before(document.createTextNode('\n-----Original Message-----\n'));
  document.body.querySelectorAll('br').forEach((node) => node.replaceWith(document.createTextNode('\n')));
  document.body.querySelectorAll('address, article, aside, div, footer, h1, h2, h3, h4, h5, h6, header, li, main, ol, p, pre, section, table, tr, ul, blockquote').forEach((node) => {
    node.prepend(document.createTextNode('\n'));
    node.append(document.createTextNode('\n'));
  });
  return (document.body.textContent ?? '')
    .replace(/\r/g, '')
    .replace(/[ \t\f\v]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function applyChanges(email: Email, changes: EmailStateChanges): Email {
  const now = new Date().toISOString();
  return {
    ...email,
    isRead: changes.isRead === undefined ? email.isRead : Number(changes.isRead),
    isStarred: changes.isStarred === undefined ? (email.isStarred ?? 0) : Number(changes.isStarred),
    archivedAt: changes.archived === undefined ? (email.archivedAt ?? null) : (changes.archived ? now : null),
    deletedAt: changes.deleted === undefined ? (email.deletedAt ?? null) : (changes.deleted ? now : null),
    updatedAt: now,
  };
}

export function Inbox() {
  const [emails, setEmails] = useState<Email[]>([]);
  const [contacts, setContacts] = useState<ContactMessage[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [compose, setCompose] = useState<ComposeState | null>(null);
  const [composeState, setComposeState] = useState<'idle' | 'saving' | 'saved' | 'sending'>('idle');
  const [notice, setNotice] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const folder = folderFromParams(searchParams);
  const activeThreadId = searchParams.get('thread');
  const activeContactId = searchParams.get('contact');
  const activeDraftId = searchParams.get('draft');

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const [nextEmails, state] = await Promise.all([adminApi.getEmails(), adminApi.getState()]);
      setEmails(nextEmails);
      setContacts(state.contactMessages ?? []);
      setApplications(state.applications ?? []);
    } catch {
      setNotice('Could not refresh the inbox. Check your connection and try again.');
    } finally {
      setLoaded(true);
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(''), 4200);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const identities = useMemo<EmailIdentitySources>(() => ({ applications, contacts }), [applications, contacts]);
  const allThreads = useMemo(() => buildThreads(emails, identities), [emails, identities]);
  const drafts = useMemo(
    () => emails.filter((email) => email.direction === 'draft').sort((a, b) => (b.updatedAt || b.createdAt).localeCompare(a.updatedAt || a.createdAt)),
    [emails],
  );
  const inboxThreads = useMemo(
    () => allThreads.filter((thread) => !thread.latest.deletedAt && !thread.latest.archivedAt && thread.messages.some((message) => message.direction === 'inbound')),
    [allThreads],
  );

  const folderThreads = useMemo(() => {
    if (folder === 'inbox') return inboxThreads;
    if (folder === 'starred') return allThreads.filter((thread) => thread.starred && !thread.latest.deletedAt);
    if (folder === 'sent') return allThreads.filter((thread) => !thread.latest.deletedAt && thread.messages.some((message) => message.direction === 'outbound'));
    if (folder === 'archive') return allThreads.filter((thread) => Boolean(thread.latest.archivedAt) && !thread.latest.deletedAt);
    if (folder === 'trash') return allThreads.filter((thread) => Boolean(thread.latest.deletedAt));
    return [];
  }, [allThreads, folder, inboxThreads]);

  const needle = query.trim().toLowerCase();
  const threadMatches = useCallback((thread: Thread) => `${thread.counterpart} ${thread.messages.map((message) => `${message.fromEmail} ${message.toEmail} ${message.subject} ${messageText(message)} ${(message.attachments ?? []).map((attachment) => attachment.filename ?? '').join(' ')}`).join(' ')}`.toLowerCase().includes(needle), [needle]);
  const visibleThreads = useMemo(() => needle ? folderThreads.filter(threadMatches) : folderThreads, [folderThreads, needle, threadMatches]);
  const visibleDrafts = useMemo(() => needle ? drafts.filter((draft) => `${draft.toEmail} ${draft.subject} ${draft.text}`.toLowerCase().includes(needle)) : drafts, [drafts, needle]);
  const visibleContacts = useMemo(() => needle ? contacts.filter((contact) => `${contact.firstName} ${contact.lastName} ${contact.email} ${contact.message}`.toLowerCase().includes(needle)) : contacts, [contacts, needle]);

  const activeThread = allThreads.find((thread) => thread.threadId === activeThreadId) ?? null;
  const activeContact = contacts.find((contact) => contact.id === activeContactId) ?? null;
  const activeDraft = drafts.find((draft) => draft.id === activeDraftId) ?? null;
  const detailOpen = Boolean(activeThread || activeContact || activeDraft);

  const folderCounts = useMemo<Record<MailFolder, number>>(() => ({
    inbox: inboxThreads.filter((thread) => thread.unread).length,
    starred: allThreads.filter((thread) => thread.starred && !thread.latest.deletedAt).length,
    sent: allThreads.filter((thread) => !thread.latest.deletedAt && thread.messages.some((message) => message.direction === 'outbound')).length,
    drafts: drafts.length,
    archive: allThreads.filter((thread) => Boolean(thread.latest.archivedAt) && !thread.latest.deletedAt).length,
    trash: allThreads.filter((thread) => Boolean(thread.latest.deletedAt)).length,
    contacts: contacts.length,
  }), [allThreads, contacts.length, drafts.length, inboxThreads]);

  const setFolder = (nextFolder: MailFolder) => {
    const next = new URLSearchParams();
    if (nextFolder !== 'inbox') next.set('folder', nextFolder);
    setSearchParams(next);
    setSelected(new Set());
    setQuery('');
  };

  const closeDetail = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('thread'); next.delete('contact'); next.delete('draft');
    setSearchParams(next);
  };

  const openThread = (thread: Thread) => {
    const next = new URLSearchParams(searchParams);
    next.delete('contact'); next.delete('draft'); next.set('thread', thread.threadId);
    setSearchParams(next);
    if (thread.unread) void updateThreads([thread.threadId], { isRead: true }, false);
  };

  const openContact = (contact: ContactMessage) => {
    const next = new URLSearchParams(searchParams);
    next.delete('thread'); next.delete('draft'); next.set('contact', contact.id);
    setSearchParams(next);
  };

  const openDraft = (draft: Email) => {
    const next = new URLSearchParams(searchParams);
    next.delete('thread'); next.delete('contact'); next.set('draft', draft.id);
    setSearchParams(next);
  };

  const updateThreads = async (threadIds: string[], changes: EmailStateChanges, announce = true) => {
    if (!threadIds.length) return;
    const previous = emails;
    const targetSet = new Set(threadIds);
    setEmails((current) => current.map((email) => targetSet.has(email.threadId) && email.direction !== 'draft' ? applyChanges(email, changes) : email));
    try {
      await adminApi.updateEmailBulkState({ threadIds }, changes);
      refreshAdminOverview();
      if (announce) setNotice('Conversation updated.');
      setSelected(new Set());
      if (activeThreadId && targetSet.has(activeThreadId) && (changes.archived !== undefined || changes.deleted !== undefined)) closeDetail();
    } catch {
      setEmails(previous);
      setNotice('That action could not be completed. Please try again.');
    }
  };

  const removeThreadsForever = async (threads: Thread[]) => {
    if (!threads.length) return;
    const prompt = threads.length === 1
      ? `Permanently delete “${threads[0].latest.subject || 'this conversation'}”? This cannot be undone.`
      : `Permanently delete ${threads.length} conversations? This cannot be undone.`;
    if (!window.confirm(prompt)) return;
    const threadIds = new Set(threads.map((thread) => thread.threadId));
    try {
      await adminApi.permanentlyDeleteEmails({ threadIds: [...threadIds] });
      refreshAdminOverview();
      setEmails((current) => current.filter((email) => !threadIds.has(email.threadId) || email.direction === 'draft'));
      setSelected(new Set());
      if (activeThreadId && threadIds.has(activeThreadId)) closeDetail();
      setNotice(`${threads.length === 1 ? 'Conversation' : 'Conversations'} permanently deleted.`);
    } catch {
      setNotice('Could not permanently delete every selected conversation. Refresh before trying again.');
      void load(true);
    }
  };

  const removeThreadForever = (thread: Thread) => removeThreadsForever([thread]);

  const deleteDraft = async (draft: Email) => {
    if (!window.confirm('Discard this draft?')) return;
    try {
      await adminApi.deleteEmailDraft(draft.id);
      refreshAdminOverview();
      setEmails((current) => current.filter((email) => email.id !== draft.id));
      closeDetail();
      setNotice('Draft discarded.');
    } catch {
      setNotice('Could not discard the draft.');
    }
  };

  const startCompose = useCallback((values: Partial<ComposeState> = {}) => {
    if (compose) {
      setNotice('Finish or close the open draft before starting another message.');
      return;
    }
    setCompose({ id: null, toEmail: '', subject: '', text: '', dirty: false, ...values });
    setComposeState('idle');
  }, [compose]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing = target?.matches('input, textarea, select, [contenteditable="true"]');
      if (event.key === '/' && !typing && !compose) {
        event.preventDefault();
        searchRef.current?.focus();
      }
      if (event.key.toLowerCase() === 'c' && !typing && !compose) {
        event.preventDefault();
        startCompose();
      }
      if (event.key === 'Escape' && detailOpen && !compose && !typing) closeDetail();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [compose, detailOpen, startCompose]);

  const listCount = folder === 'drafts' ? visibleDrafts.length : folder === 'contacts' ? visibleContacts.length : visibleThreads.length;
  const allVisibleSelected = visibleThreads.length > 0 && visibleThreads.every((thread) => selected.has(thread.threadId));
  const selectedThreads = folderThreads.filter((thread) => selected.has(thread.threadId));
  const selectedAllStarred = selectedThreads.length > 0 && selectedThreads.every((thread) => thread.starred);

  const toggleSelected = (threadId: string) => setSelected((current) => {
    const next = new Set(current);
    if (next.has(threadId)) next.delete(threadId); else next.add(threadId);
    return next;
  });

  const selectAllVisible = () => setSelected((current) => {
    if (allVisibleSelected) return new Set();
    const next = new Set(current);
    visibleThreads.forEach((thread) => next.add(thread.threadId));
    return next;
  });

  const openComposerForDraft = (draft: Email) => startCompose({ id: draft.id, threadId: draft.threadId, toEmail: draft.toEmail, subject: draft.subject, text: draft.text });
  const visibleLabel = folder === 'contacts' ? 'website inquiries' : folder === 'drafts' ? 'drafts' : 'conversations';

  return (
    <div className="cms-page cms-page-full cms-mail-page">
      <div className={`cms-mail-shell ${detailOpen ? 'has-detail' : ''}`}>
        <aside className="cms-mail-folders" aria-label="Mail folders">
          <button type="button" className="cms-mail-compose-btn" onClick={() => startCompose()}><PenLine size={16} />Compose</button>
          <nav>
            {FOLDERS.map(({ id, label, icon: Icon }) => (
              <button key={id} type="button" className={folder === id ? 'active' : ''} aria-current={folder === id ? 'page' : undefined} onClick={() => setFolder(id)}>
                <Icon size={16} fill={id === 'starred' && folder === id ? 'currentColor' : 'none'} />
                <span>{label}</span>
                {folderCounts[id] > 0 && <b>{folderCounts[id]}</b>}
              </button>
            ))}
          </nav>
          <p><kbd>C</kbd> compose · <kbd>/</kbd> search</p>
        </aside>

        <section className="cms-mail-list-pane" aria-label={`${FOLDERS.find((item) => item.id === folder)?.label} list`}>
          <div className="cms-mail-searchbar">
            <label>
              <Search size={15} aria-hidden="true" />
              <input ref={searchRef} value={query} onChange={(event) => setQuery(event.target.value)} placeholder={`Search ${visibleLabel}`} aria-label={`Search ${visibleLabel}`} />
              {query && <button type="button" aria-label="Clear search" onClick={() => setQuery('')}><X size={14} /></button>}
            </label>
            <button type="button" className="cms-mail-icon-btn" aria-label="Refresh inbox" title="Refresh" disabled={loading} onClick={() => void load()}><RefreshCw size={16} className={loading ? 'is-spinning' : ''} /></button>
          </div>

          <div className="cms-mail-list-toolbar">
            {folder !== 'drafts' && folder !== 'contacts' ? (
              <>
                <label className="cms-mail-select-all" title={allVisibleSelected ? 'Clear selection' : 'Select all'}>
                  <input type="checkbox" checked={allVisibleSelected} onChange={selectAllVisible} aria-label={allVisibleSelected ? 'Clear selection' : 'Select all visible conversations'} />
                </label>
                {selected.size > 0 ? (
                  <div className="cms-mail-bulk-actions">
                    <b>{selected.size} selected</b>
                    <button type="button" onClick={() => void updateThreads([...selected], { isRead: true })} title="Mark as read" aria-label="Mark selected as read"><MailOpen size={15} /></button>
                    <button type="button" onClick={() => void updateThreads([...selected], { isRead: false })} title="Mark as unread" aria-label="Mark selected as unread"><Mail size={15} /></button>
                    <button type="button" onClick={() => void updateThreads([...selected], { isStarred: !selectedAllStarred })} title={selectedAllStarred ? 'Unstar' : 'Star'} aria-label={selectedAllStarred ? 'Unstar selected' : 'Star selected'}><Star size={15} fill={selectedAllStarred ? 'currentColor' : 'none'} /></button>
                    {folder === 'trash' ? (
                      <>
                        <button type="button" onClick={() => void updateThreads([...selected], { deleted: false, archived: false })} title="Restore" aria-label="Restore selected"><RotateCcw size={15} /></button>
                        <button type="button" onClick={() => void removeThreadsForever(selectedThreads)} title="Delete forever" aria-label="Permanently delete selected"><Trash2 size={15} /></button>
                      </>
                    ) : (
                      <>
                        <button type="button" onClick={() => void updateThreads([...selected], { archived: true })} title="Archive" aria-label="Archive selected"><Archive size={15} /></button>
                        <button type="button" onClick={() => void updateThreads([...selected], { deleted: true })} title="Move to trash" aria-label="Move selected to trash"><Trash2 size={15} /></button>
                      </>
                    )}
                  </div>
                ) : <span>{listCount} {visibleLabel}</span>}
              </>
            ) : <span>{listCount} {visibleLabel}</span>}
          </div>

          <div className="cms-mail-list">
            {!loaded && <MailListSkeleton />}
            {loaded && listCount === 0 && <MailEmpty folder={folder} searching={Boolean(needle)} />}
            {loaded && folder !== 'drafts' && folder !== 'contacts' && visibleThreads.map((thread) => (
              <ThreadListItem
                key={thread.threadId}
                thread={thread}
                active={activeThreadId === thread.threadId}
                selected={selected.has(thread.threadId)}
                onOpen={() => openThread(thread)}
                onSelect={() => toggleSelected(thread.threadId)}
                onStar={() => void updateThreads([thread.threadId], { isStarred: !thread.starred })}
              />
            ))}
            {loaded && folder === 'drafts' && visibleDrafts.map((draft) => (
              <DraftListItem key={draft.id} draft={draft} active={draft.id === activeDraftId} onOpen={() => openDraft(draft)} onEdit={() => openComposerForDraft(draft)} />
            ))}
            {loaded && folder === 'contacts' && visibleContacts.map((contact) => (
              <ContactListItem key={contact.id} contact={contact} active={contact.id === activeContactId} onOpen={() => openContact(contact)} />
            ))}
          </div>
        </section>

        <section className="cms-mail-detail" aria-label="Message detail">
          {activeThread ? (
            <ThreadView
              key={activeThread.threadId}
              thread={activeThread}
              folder={folder}
              onBack={closeDetail}
              onState={(changes) => updateThreads([activeThread.threadId], changes)}
              onDeleteForever={() => removeThreadForever(activeThread)}
              onReply={() => {
                const inbound = [...activeThread.messages].reverse().find((message) => message.direction === 'inbound');
                const recipient = inbound ? (inbound.replyTo || inbound.fromEmail) : activeThread.latest.toEmail;
                startCompose({ threadId: activeThread.threadId, toEmail: recipient, subject: /^re:/i.test(activeThread.latest.subject) ? activeThread.latest.subject : `Re: ${activeThread.latest.subject}` });
              }}
              onForward={() => startCompose({ subject: /^fwd:/i.test(activeThread.latest.subject) ? activeThread.latest.subject : `Fwd: ${activeThread.latest.subject}`, text: `\n\n---------- Forwarded message ----------\nFrom: ${activeThread.counterpart}\nDate: ${new Date(activeThread.latest.createdAt).toLocaleString()}\nSubject: ${activeThread.latest.subject}\n\n${messageText(activeThread.latest)}` })}
              identities={identities}
            />
          ) : activeContact ? (
            <ContactView key={activeContact.id} contact={activeContact} onBack={closeDetail} onReply={() => startCompose({ threadId: activeContact.id, toEmail: activeContact.email, subject: 'Re: Your Sugarloaf Goldens inquiry', text: `Hi ${activeContact.firstName},\n\nThank you for reaching out to Sugarloaf Goldens.\n\n` })} />
          ) : activeDraft ? (
            <DraftView draft={activeDraft} onBack={closeDetail} onEdit={() => openComposerForDraft(activeDraft)} onDelete={() => deleteDraft(activeDraft)} />
          ) : (
            <div className="cms-mail-placeholder">
              <span><Mail size={24} /></span>
              <h2>Your conversations live here</h2>
              <p>Select a message to read and reply, or use Compose to start a new email.</p>
            </div>
          )}
        </section>
      </div>

      {compose && (
        <ComposeWindow
          value={compose}
          state={composeState}
          onChange={(changes) => setCompose((current) => current ? { ...current, ...changes, dirty: true } : current)}
          onState={setComposeState}
          onSaved={(saved, unchanged) => {
            setCompose((current) => current ? { ...current, id: saved.id, threadId: saved.threadId, dirty: unchanged ? false : current.dirty } : current);
            setEmails((current) => [...current.filter((email) => email.id !== saved.id), saved]);
            refreshAdminOverview();
          }}
          onClose={() => setCompose(null)}
          onSent={(message, delivered) => {
            setCompose(null);
            setNotice(delivered ? 'Email sent.' : 'Email saved, but delivery is not configured.');
            setEmails((current) => [...current.filter((email) => email.id !== message.id), message]);
            refreshAdminOverview();
            void load(true);
          }}
          onError={(message) => setNotice(message)}
        />
      )}
      {notice && <div className="cms-toast" role="status">{notice}</div>}
    </div>
  );
}

function ThreadListItem({ thread, active, selected, onOpen, onSelect, onStar }: { thread: Thread; active: boolean; selected: boolean; onOpen: () => void; onSelect: () => void; onStar: () => void }) {
  const attachments = thread.messages.reduce((total, message) => total + (message.attachments?.length ?? 0), 0);
  const previewParts = splitEmailBody(messageText(thread.latest));
  const preview = previewParts.currentText.trim() || (previewParts.hasQuotedText ? 'Quoted message' : '(no message body)');
  return (
    <article className={`cms-mail-row ${active ? 'active' : ''} ${thread.unread ? 'unread' : ''}`}>
      <label className="cms-mail-row-check"><input type="checkbox" checked={selected} onChange={onSelect} aria-label={`Select ${thread.latest.subject}`} /></label>
      <button type="button" className={`cms-mail-star ${thread.starred ? 'active' : ''}`} onClick={onStar} aria-label={thread.starred ? `Unstar ${thread.latest.subject}` : `Star ${thread.latest.subject}`} title={thread.starred ? 'Unstar' : 'Star'}><Star size={15} fill={thread.starred ? 'currentColor' : 'none'} /></button>
      <button type="button" className="cms-mail-row-main" onClick={onOpen} aria-current={active ? 'true' : undefined}>
        <span className="cms-mail-row-top"><b title={thread.counterpartEmail}>{thread.counterpart || '(unknown sender)'}</b><time>{formatListDate(thread.latest.createdAt)}</time></span>
        <span className="cms-mail-row-subject">{thread.latest.subject || '(no subject)'}{thread.messages.length > 1 && <em>{thread.messages.length}</em>}</span>
        <span className="cms-mail-row-preview">{attachments > 0 && <Paperclip size={11} aria-label={`${attachments} attachments`} />}{preview}</span>
      </button>
    </article>
  );
}

function DraftListItem({ draft, active, onOpen, onEdit }: { draft: Email; active: boolean; onOpen: () => void; onEdit: () => void }) {
  return (
    <article className={`cms-mail-row cms-mail-draft-row ${active ? 'active' : ''}`}>
      <button type="button" className="cms-mail-row-main" onClick={onOpen} aria-current={active ? 'true' : undefined}>
        <span className="cms-mail-row-top"><b><em>Draft</em> {draft.toEmail || 'No recipient'}</b><time>{formatListDate(draft.updatedAt || draft.createdAt)}</time></span>
        <span className="cms-mail-row-subject">{draft.subject || '(no subject)'}</span>
        <span className="cms-mail-row-preview">{draft.text || 'Empty draft'}</span>
      </button>
      <button type="button" className="cms-mail-row-edit" onClick={onEdit}>Continue</button>
    </article>
  );
}

function ContactListItem({ contact, active, onOpen }: { contact: ContactMessage; active: boolean; onOpen: () => void }) {
  return (
    <article className={`cms-mail-row cms-mail-contact-row ${active ? 'active' : ''}`}>
      <button type="button" className="cms-mail-row-main" onClick={onOpen} aria-current={active ? 'true' : undefined}>
        <span className="cms-mail-row-top"><b>{contact.firstName} {contact.lastName}</b><time>{formatListDate(contact.createdAt)}</time></span>
        <span className="cms-mail-row-subject">Website inquiry</span>
        <span className="cms-mail-row-preview">{contact.message}</span>
      </button>
    </article>
  );
}

function MailListSkeleton() {
  return <div className="cms-mail-list-skeleton" aria-label="Loading messages">{[0, 1, 2, 3].map((item) => <span key={item} />)}</div>;
}

function MailEmpty({ folder, searching }: { folder: MailFolder; searching: boolean }) {
  const message = searching ? 'No messages match your search.' : folder === 'inbox' ? 'You are all caught up.' : folder === 'drafts' ? 'No saved drafts.' : folder === 'contacts' ? 'No website inquiries yet.' : `Nothing in ${folder}.`;
  return (
    <div className="cms-mail-list-empty">
      <Check size={18} />
      <b>{message}</b>
    </div>
  );
}

function ThreadView({ thread, folder, identities, onBack, onState, onDeleteForever, onReply, onForward }: { thread: Thread; folder: MailFolder; identities: EmailIdentitySources; onBack: () => void; onState: (changes: EmailStateChanges) => void; onDeleteForever: () => void; onReply: () => void; onForward: () => void }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const inTrash = folder === 'trash' || Boolean(thread.latest.deletedAt);
  const archived = Boolean(thread.latest.archivedAt);

  return (
    <div className="cms-thread-view cms-mail-thread-view">
      <header className="cms-mail-detail-head">
        <button type="button" className="cms-mail-icon-btn cms-mail-back" aria-label="Back to message list" onClick={onBack}><ArrowLeft size={17} /></button>
        <div><h2>{thread.latest.subject || '(no subject)'}</h2><p>{thread.counterpart}{thread.counterpart !== thread.counterpartEmail ? ` · ${thread.counterpartEmail}` : ''} · {thread.messages.length} message{thread.messages.length === 1 ? '' : 's'}</p></div>
        <div className="cms-mail-detail-actions">
          <button type="button" className={`cms-mail-icon-btn ${thread.starred ? 'is-starred' : ''}`} aria-label={thread.starred ? 'Unstar conversation' : 'Star conversation'} title={thread.starred ? 'Unstar' : 'Star'} onClick={() => onState({ isStarred: !thread.starred })}><Star size={16} fill={thread.starred ? 'currentColor' : 'none'} /></button>
          {inTrash ? (
            <button type="button" className="cms-mail-icon-btn" aria-label="Restore conversation" title="Restore" onClick={() => onState({ deleted: false, archived: false })}><RotateCcw size={16} /></button>
          ) : (
            <button type="button" className="cms-mail-icon-btn" aria-label={archived ? 'Move to inbox' : 'Archive conversation'} title={archived ? 'Move to inbox' : 'Archive'} onClick={() => onState({ archived: !archived })}>{archived ? <InboxIcon size={16} /> : <Archive size={16} />}</button>
          )}
          <div className="cms-mail-more">
            <button type="button" className="cms-mail-icon-btn" aria-label="More conversation actions" aria-expanded={moreOpen} onClick={() => setMoreOpen((open) => !open)}><MoreHorizontal size={18} /></button>
            {moreOpen && <div className="cms-mail-more-menu">
              <button type="button" onClick={() => { setMoreOpen(false); onState({ isRead: thread.unread }); }}>{thread.unread ? <MailOpen size={14} /> : <Mail size={14} />}{thread.unread ? 'Mark as read' : 'Mark as unread'}</button>
              <button type="button" onClick={() => { setMoreOpen(false); onForward(); }}><Forward size={14} />Forward</button>
              {inTrash ? <button type="button" className="danger" onClick={() => { setMoreOpen(false); onDeleteForever(); }}><Trash2 size={14} />Delete forever</button> : <button type="button" className="danger" onClick={() => { setMoreOpen(false); onState({ deleted: true }); }}><Trash2 size={14} />Move to trash</button>}
            </div>}
          </div>
        </div>
      </header>
      <div className="cms-messages">
        {thread.messages.map((message) => {
          const parts = splitEmailBody(messageText(message));
          const currentText = parts.currentText.trim();
          const senderName = message.direction === 'inbound'
            ? resolveEmailDisplayName(message, identities)
            : 'Sugarloaf Goldens';
          return (
            <article key={message.id} className={`cms-message cms-message-${message.direction}`}>
              <div className="cms-message-meta"><span>{senderName}</span><time>{new Date(message.createdAt).toLocaleString()}</time></div>
              <small className="cms-message-route">{message.direction === 'inbound' && senderName !== message.fromEmail ? `${message.fromEmail} · ` : ''}To {message.toEmail}</small>
              <p className="cms-message-body">{currentText || (parts.hasQuotedText ? 'No new message text.' : '(no text body)')}</p>
              {parts.hasQuotedText && (
                <details className="cms-message-quote">
                  <summary><span>Show quoted message</span><ChevronDown size={14} aria-hidden="true" /></summary>
                  <p>{parts.quotedText.trim()}</p>
                </details>
              )}
              <MessageAttachments emailId={message.id} attachments={message.attachments ?? []} />
            </article>
          );
        })}
      </div>
      {!inTrash && (
        <div className="cms-mail-detail-footer">
          <button type="button" className="cms-btn cms-btn-primary" onClick={onReply}><Reply size={14} />Reply to {thread.counterpart}</button>
          <button type="button" className="cms-btn" onClick={onForward}><Forward size={14} />Forward</button>
          <span>Replies autosave safely in Drafts.</span>
        </div>
      )}
    </div>
  );
}

function DraftView({ draft, onBack, onEdit, onDelete }: { draft: Email; onBack: () => void; onEdit: () => void; onDelete: () => void }) {
  return (
    <div className="cms-thread-view cms-mail-thread-view">
      <header className="cms-mail-detail-head">
        <button type="button" className="cms-mail-icon-btn cms-mail-back" aria-label="Back to draft list" onClick={onBack}><ArrowLeft size={17} /></button>
        <div><h2>{draft.subject || '(no subject)'}</h2><p>Draft to {draft.toEmail || 'no recipient'} · saved {new Date(draft.updatedAt || draft.createdAt).toLocaleString()}</p></div>
        <div className="cms-mail-detail-actions"><button type="button" className="cms-mail-icon-btn" aria-label="Discard draft" title="Discard draft" onClick={onDelete}><Trash2 size={16} /></button></div>
      </header>
      <article className="cms-mail-draft-preview"><span>To: {draft.toEmail || 'No recipient'}</span><p>{draft.text || 'This draft is empty.'}</p></article>
      <div className="cms-mail-draft-actions"><button type="button" className="cms-btn cms-btn-primary" onClick={onEdit}><PenLine size={14} />Continue writing</button></div>
    </div>
  );
}

function ContactView({ contact, onBack, onReply }: { contact: ContactMessage; onBack: () => void; onReply: () => void }) {
  return (
    <div className="cms-thread-view cms-mail-thread-view">
      <header className="cms-mail-detail-head">
        <button type="button" className="cms-mail-icon-btn cms-mail-back" aria-label="Back to inquiry list" onClick={onBack}><ArrowLeft size={17} /></button>
        <div><h2>{contact.firstName} {contact.lastName}</h2><p>{contact.email} · website inquiry · {new Date(contact.createdAt).toLocaleDateString()}</p></div>
      </header>
      <article className="cms-contact-message"><span>Original message</span><p>{contact.message}</p></article>
      <div className="cms-mail-detail-footer"><button type="button" className="cms-btn cms-btn-primary" onClick={onReply}><Reply size={14} />Reply to {contact.firstName}</button><span>Your reply will autosave safely in Drafts.</span></div>
    </div>
  );
}

function ComposeWindow({ value, state, onChange, onState, onSaved, onClose, onSent, onError }: { value: ComposeState; state: 'idle' | 'saving' | 'saved' | 'sending'; onChange: (changes: Partial<ComposeState>) => void; onState: (state: 'idle' | 'saving' | 'saved' | 'sending') => void; onSaved: (saved: Email, unchanged: boolean) => void; onClose: () => void; onSent: (message: Email, delivered: boolean) => void; onError: (message: string) => void }) {
  const latestRef = useRef(value);
  latestRef.current = value;
  const draftIdRef = useRef(value.id);
  if (value.id) draftIdRef.current = value.id;
  const inFlightRef = useRef<Promise<Email | null> | null>(null);
  const lastSavedRef = useRef<{ signature: string; email: Email } | null>(null);
  const sendingRef = useRef(false);
  const dismissedRef = useRef(false);
  const [inlineError, setInlineError] = useState('');
  const signature = (snapshot: ComposeState) => JSON.stringify([snapshot.threadId ?? '', snapshot.toEmail.trim(), snapshot.subject.trim(), snapshot.text]);

  const persist = useCallback(async (snapshot: ComposeState, source: 'autosave' | 'manual' | 'send' | 'unmount' = 'manual'): Promise<Email | null> => {
    const hasContent = Boolean(snapshot.toEmail.trim() || snapshot.subject.trim() || snapshot.text.trim());
    if (!hasContent && !snapshot.id) return null;
    if (source === 'autosave' && sendingRef.current) return null;

    const pending = inFlightRef.current;
    if (pending) {
      await pending;
      if (inFlightRef.current === pending) inFlightRef.current = null;
    }
    const snapshotSignature = signature(snapshot);
    if (lastSavedRef.current?.signature === snapshotSignature) {
      const cached = lastSavedRef.current.email;
      if (source !== 'unmount') {
        onSaved(cached, true);
        if (!sendingRef.current) onState('saved');
        setInlineError('');
      }
      return cached;
    }
    if (source === 'autosave' && sendingRef.current) return null;

    if (!sendingRef.current && source !== 'unmount') onState('saving');
    const payload: EmailDraftInput = { threadId: snapshot.threadId, toEmail: snapshot.toEmail.trim(), subject: snapshot.subject.trim(), text: snapshot.text, html: null };
    const operation = (async () => {
      try {
        const draftId = draftIdRef.current ?? snapshot.id;
        const saved = draftId ? await adminApi.updateEmailDraft(draftId, payload) : await adminApi.createEmailDraft(payload);
        draftIdRef.current = saved.id;
        lastSavedRef.current = { signature: snapshotSignature, email: saved };
        const current = latestRef.current;
        const unchanged = signature(current) === snapshotSignature;
        if (source !== 'unmount') {
          onSaved(saved, unchanged);
          if (!sendingRef.current) onState('saved');
          setInlineError('');
        }
        return saved;
      } catch {
        const message = 'Could not save the draft. Your text is still open.';
        if (source !== 'unmount') {
          if (!sendingRef.current) onState('idle');
          setInlineError(message);
          onError(message);
        }
        return null;
      }
    })();
    inFlightRef.current = operation;
    try {
      return await operation;
    } finally {
      if (inFlightRef.current === operation) inFlightRef.current = null;
    }
  }, [onError, onSaved, onState]);

  useEffect(() => {
    if (!value.dirty || state === 'saving' || state === 'sending') return;
    const snapshot = value;
    const timer = window.setTimeout(() => { void persist(snapshot, 'autosave'); }, 800);
    return () => window.clearTimeout(timer);
  }, [persist, state, value]);

  const close = async () => {
    if (sendingRef.current) return;
    if (value.dirty) {
      const hasContent = Boolean(value.toEmail.trim() || value.subject.trim() || value.text.trim());
      const saved = await persist(value, 'manual');
      if (hasContent && !saved) return;
    }
    dismissedRef.current = true;
    onClose();
  };

  const persistRef = useRef(persist);
  persistRef.current = persist;
  const closeRef = useRef(close);
  closeRef.current = close;

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (dismissedRef.current || !latestRef.current.dirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || sendingRef.current) return;
      event.preventDefault();
      event.stopPropagation();
      void closeRef.current();
    };
    window.addEventListener('beforeunload', warn);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('beforeunload', warn);
      window.removeEventListener('keydown', onKeyDown);
      const snapshot = latestRef.current;
      if (!dismissedRef.current && snapshot.dirty && !sendingRef.current) void persistRef.current(snapshot, 'unmount');
    };
  }, []);

  const send = async (event: FormEvent) => {
    event.preventDefault();
    if (sendingRef.current) return;
    if (!value.toEmail.trim() || !value.subject.trim() || !value.text.trim()) return;
    sendingRef.current = true;
    onState('sending');
    let draftId = value.id;
    if (!value.id || value.dirty || inFlightRef.current) draftId = (await persist(value, 'send'))?.id ?? null;
    if (!draftId) { sendingRef.current = false; onState('idle'); return; }
    try {
      const result = await adminApi.sendEmailDraft(draftId);
      dismissedRef.current = true;
      onSent(result.email, result.delivered);
    } catch {
      sendingRef.current = false;
      const message = 'Could not send the email. The draft is saved so nothing is lost.';
      onState('saved');
      setInlineError(message);
      onError(message);
    }
  };

  const change = (changes: Partial<ComposeState>) => {
    setInlineError('');
    onChange(changes);
  };

  return (
    <section className="cms-mail-compose-window" role="dialog" aria-modal="false" aria-labelledby="cms-compose-title">
      <header><h2 id="cms-compose-title">New message</h2><span>{state === 'saving' ? 'Saving…' : state === 'saved' ? 'Draft saved' : state === 'sending' ? 'Sending…' : ''}</span><button type="button" aria-label="Save and close" title="Save and close" disabled={state === 'saving' || state === 'sending'} onClick={() => void close()}><X size={17} /></button></header>
      <form onSubmit={send}>
        <label className="cms-mail-compose-line"><span>To</span><input autoFocus type="email" value={value.toEmail} onChange={(event) => change({ toEmail: event.target.value })} placeholder="name@example.com" disabled={state === 'sending'} required /></label>
        <label className="cms-mail-compose-line"><span>Subject</span><input value={value.subject} onChange={(event) => change({ subject: event.target.value })} placeholder="Subject" disabled={state === 'sending'} required /></label>
        <textarea value={value.text} onChange={(event) => change({ text: event.target.value })} placeholder="Write your message…" aria-label="Message body" disabled={state === 'sending'} required />
        {inlineError && <p className="cms-mail-compose-error" role="alert">{inlineError}</p>}
        <footer><span>{value.id ? 'Saved to Drafts' : 'Autosaves as you type'}</span><button type="submit" className="cms-btn cms-btn-primary" disabled={state === 'saving' || state === 'sending' || !value.toEmail.trim() || !value.subject.trim() || !value.text.trim()}>{state === 'sending' ? 'Sending…' : 'Send'}<Send size={14} /></button></footer>
      </form>
    </section>
  );
}
